import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Autocomplete, TextField, CircularProgress, Box, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { getTaxMasters } from '../api/taxMaster';
import { filterActiveMasters, isMasterActive, inactiveMasterMessage } from '../utils/masterEntity';
import { inputSx } from '../theme';

const PAGE_SIZE = 200; // API max for query.limit

function normalizeList(data) {
  return Array.isArray(data) ? data : (data?.data || data?.items || []);
}

async function fetchAllActiveHsn() {
  const all = [];
  let offset = 0;
  for (;;) {
    const data = await getTaxMasters({ is_active: true, limit: PAGE_SIZE, offset });
    const chunk = filterActiveMasters(normalizeList(data));
    all.push(...chunk);
    if (chunk.length < PAGE_SIZE) break;
    offset += PAGE_SIZE;
    if (offset >= 2000) break;
  }
  return all;
}

function hsnOptionLabel(item) {
  return item?.hsn_code || '';
}

function fieldErrorMessage(error) {
  if (!error) return undefined;
  return error.message || String(error);
}

function filterHsnOptions(opts, { inputValue: raw }, selectedItem) {
  const q = (raw || '').trim().toLowerCase();
  if (!q) return opts;
  // Opening with a selected value should still show the full active list.
  if (selectedItem && q === hsnOptionLabel(selectedItem).toLowerCase()) return opts;
  return opts.filter((item) => {
    const code = (item.hsn_code || '').toLowerCase();
    const desc = (item.description || '').toLowerCase();
    return code.includes(q) || desc.includes(q);
  });
}

/**
 * Loads active Tax Master HSN entries when the popup opens (no typing required).
 * Further typing filters that list by HSN code or description.
 * onChange(hsnCode, record) hands back the full record so callers can
 * auto-fill gst_rate/description from it.
 *
 * Pass `valueRecord` when hydrating an existing value (e.g. edit mode) so the
 * selection renders immediately without waiting on a search — see ItemForm.js.
 */
export default function HsnAutocomplete({
  value,
  valueRecord = null,
  onChange,
  label = 'HSN Code',
  placeholder = 'Search HSN code or description…',
  disabled = false,
  error,
  helperText,
  sx,
}) {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [selectedItem, setSelectedItem] = useState(valueRecord);
  const [open, setOpen] = useState(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!value) {
      setSelectedItem(null);
      return;
    }
    if (selectedItem && selectedItem.hsn_code === value) return;
    const found = options.find((item) => item.hsn_code === value) || valueRecord || null;
    if (found) setSelectedItem(found);
  }, [value, options, selectedItem, valueRecord]);

  useEffect(() => {
    if (!open) return undefined;

    const requestId = ++requestIdRef.current;
    let cancelled = false;

    async function fetchOptions() {
      setLoading(true);
      setFetchError(null);
      try {
        // API: GET /inventory/tax-masters/?is_active=true&limit=200
        const list = await fetchAllActiveHsn();
        if (cancelled || requestId !== requestIdRef.current) return;
        setOptions(list);
      } catch (err) {
        if (cancelled || requestId !== requestIdRef.current) return;
        setFetchError(err?.message || 'Failed to load HSN list');
        setOptions([]);
      } finally {
        if (!cancelled && requestId === requestIdRef.current) setLoading(false);
      }
    }

    fetchOptions();
    return () => { cancelled = true; };
  }, [open]);

  const displayOptions = useMemo(() => {
    const list = filterActiveMasters(options);
    if (!selectedItem) return list;
    if (list.some((item) => item.hsn_code === selectedItem.hsn_code)) return list;
    if (!isMasterActive(selectedItem)) return list;
    return [selectedItem, ...list];
  }, [options, selectedItem]);

  const noOptionsText = (() => {
    if (loading) return 'Loading…';
    if (fetchError) return fetchError;
    return 'No matching HSN found';
  })();

  return (
    <Autocomplete
      size="small"
      fullWidth
      disabled={disabled}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={displayOptions}
      loading={loading}
      value={selectedItem}
      inputValue={inputValue}
      onInputChange={(_, newInput, reason) => {
        if (reason === 'input' || reason === 'clear' || reason === 'reset') {
          setInputValue(newInput);
          if (
            (reason === 'input' || reason === 'clear')
            && selectedItem
            && newInput.trim() !== hsnOptionLabel(selectedItem)
          ) {
            setSelectedItem(null);
            onChange('', null);
          }
        }
      }}
      onChange={(_, item) => {
        if (item && !isMasterActive(item)) {
          toast.error(inactiveMasterMessage(item.hsn_code ? `HSN ${item.hsn_code}` : 'This HSN'));
          return;
        }
        setSelectedItem(item);
        onChange(item?.hsn_code || '', item);
      }}
      getOptionLabel={hsnOptionLabel}
      isOptionEqualToValue={(a, b) => a?.hsn_code === b?.hsn_code}
      filterOptions={(opts, state) => filterHsnOptions(opts, state, selectedItem)}
      noOptionsText={noOptionsText}
      renderOption={(props, item) => (
        <Box component="li" {...props} key={item.hsn_code}>
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{item.hsn_code}</Typography>
            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
              {[item.description, item.gst_rate != null ? `GST ${item.gst_rate}%` : null]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          InputLabelProps={label ? { shrink: true } : undefined}
          error={Boolean(error)}
          helperText={fieldErrorMessage(error) || helperText}
          sx={{ ...inputSx, ...sx }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
